import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Archive, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@nexus/ui";
import { SettingsField } from "./shared/SettingsRow";

function summarizeRetoolId(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (trimmed.length <= 14) return trimmed;
  return `${trimmed.slice(0, 8)}…${trimmed.slice(-12)}`;
}

export interface LegacyRetoolSettingsRowProps {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  error: string | null;
  touched: boolean;
  disabled?: boolean;
}

/** Collapsed one-line footer for the deprecated Retool user ID; expands in place to edit. */
export function LegacyRetoolSettingsRow({
  value,
  onChange,
  onBlur,
  error,
  touched,
  disabled = false,
}: LegacyRetoolSettingsRowProps) {
  const { t } = useTranslation();
  const hasError = touched && !!error;
  const [expanded, setExpanded] = useState(hasError);
  const summary = summarizeRetoolId(value);

  useEffect(() => {
    if (hasError) setExpanded(true);
  }, [hasError]);

  return (
    <div
      className="mx-8 mb-6 flex flex-col gap-3 border-t border-border-subtle pt-4"
      data-testid="org-settings-legacy-retool-row"
    >
      <div className="flex min-w-0 items-center gap-2">
        <Archive className="size-3.5 shrink-0 text-muted-foreground/70" aria-hidden />
        <span className="shrink-0 text-[11px] text-muted-foreground">
          {t("iam.orgDrawer.settings.legacyRetoolTitle", "Legacy · Retool user ID")}
        </span>
        <span className="shrink-0 rounded-sm bg-border-subtle px-1.5 py-[3px] font-mono text-[9px] uppercase leading-none text-muted-foreground">
          {t("iam.orgDrawer.settings.legacyRetoolDeprecated", "Deprecated")}
        </span>
        <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-muted-foreground/70">
          {expanded ? null : summary || t("iam.orgDrawer.settings.legacyRetoolEmpty", "Not set")}
        </span>
        <button
          type="button"
          disabled={disabled}
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
          className="inline-flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
        >
          {expanded
            ? t("iam.orgDrawer.settings.legacyRetoolHide", "Hide")
            : t("iam.orgDrawer.settings.legacyRetoolShow", "Show")}
          {expanded ? <ChevronDown className="size-3" aria-hidden /> : <ChevronRight className="size-3" aria-hidden />}
        </button>
      </div>
      {expanded && (
        <SettingsField
          label={t("iam.orgDrawer.settings.retoolUserIdLabel", "Retool user ID")}
          htmlFor="org-settings-retool-user-id"
          hint={hasError ? undefined : t(
            "iam.orgDrawer.settings.retoolUserIdOptionalHint",
            "Optional · must be a valid UUID if provided",
          )}
          error={hasError ? error : undefined}
          className="max-w-[420px]"
        >
          <input
            id="org-settings-retool-user-id"
            type="text"
            data-testid="org-settings-retool-user-id"
            placeholder={t(
              "iam.orgDrawer.settings.retoolUserIdPlaceholder",
              "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
            )}
            value={value}
            onChange={(e) => onChange(e.target.value.trim())}
            onBlur={onBlur}
            disabled={disabled}
            className={cn(
              "h-9 rounded-md border bg-background px-3 font-mono text-xs text-foreground outline-none placeholder:font-sans placeholder:text-muted-foreground/60 focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50",
              hasError ? "border-destructive focus:border-destructive" : "border-input-border focus:border-ring",
            )}
          />
        </SettingsField>
      )}
    </div>
  );
}
